import styled from "@emotion/styled";
import { Theme } from "@emotion/react";
import { MDSComboboxVariant } from "./combobox.types";
import { MDSPopover } from "@/mds/components/overlays";

export const StyledCombobox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  position: relative;
  width: 100%;
`;

export const StyledLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.grey.x700};
  line-height: ${({ theme }) => theme.lineHeights.small};
`;

export const StyledInputContainer = styled.div<{
  variant?: MDSComboboxVariant;
  hasLeftActionButton?: boolean;
}>`
  display: flex;
  position: relative;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme, variant, hasLeftActionButton }) =>
    getPadding(theme, variant, hasLeftActionButton)};
  border: 1px solid ${({ theme, variant }) => getBorderColor(theme, variant)};
  border-radius: ${({ theme, variant }) => getBorderRadius(theme, variant)};
  background: ${({ theme, variant }) => getBackgroundColor(theme, variant)};
  min-height: 32px;
  transition:
    border-color 0.3s ease-out,
    background 0.3s ease-out;

  &:focus-within {
    border-color: ${({ theme, variant }) => getFocusBorderColor(theme, variant)};
    background: ${({ theme, variant }) => getFocusBackgroundColor(theme, variant)};
  }

  &:hover:not(:focus-within):not([aria-disabled="true"]) {
    border-color: ${({ theme, variant }) => getHoverBorderColor(theme, variant)};
  }

  &[aria-disabled="true"] {
    background: ${({ theme }) => theme.colors.grey.x100};
    border-color: ${({ theme }) => theme.colors.grey.x300};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const StyledLeftContainer = styled.div`
  display: flex;
  align-items: flex-start;

  min-height: 32px;

  &:empty {
    display: none;
  }
`;

export const StyledRightContainer = styled.div<{
  $variant?: MDSComboboxVariant;
}>`
  display: flex;
  align-items: flex-end;
  min-height: 32px;

  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin-left: auto;

  & svg {
    height: ${({ theme }) => theme.spacing["8"]};
    width: ${({ theme }) => theme.spacing["8"]};
  }
`;

export const StyledLeftIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  > div {
    height: 16px;
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.grey.x400};
  }
`;

export const StyledLeftAddonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StyledInputArea = styled.div`
  flex: 1;
  width: 100%;
  min-width: 0; /* Allow shrinking */
  min-height: 32px;

  display: flex;
  align-items: center;
  flex-direction: column;
`;

export const StyledExpand = styled.div`
  flex: 1;
  min-width: 0; /* Allow shrinking */
  width: 100%;

  /* Constrain the editor content */
  .ProseMirror {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    outline: none;
    border: none;
    background: transparent;
    padding: 2px 0;
    word-wrap: break-word;
    overflow-wrap: break-word;
    font-size: ${({ theme }) => theme.fontSizes.smedium};
    line-height: ${({ theme }) => theme.lineHeights.smedium};
    text-align: left;
    cursor: text;
    color: ${({ theme }) => theme.colors.text.primary};

    /* Prevent horizontal scrolling */
    overflow-x: hidden;

    /* Allow multi-line but limit height with scrolling */
    max-height: 120px;
    overflow-y: auto;

    /* Hide scrollbar */
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;

    & [data-placeholder] {
      color: ${({ theme }) => theme.colors.text.tertiary};
    }
  }

  /* Style the MemCommonRichTextInput container directly */
  > div {
    width: 100%;
    min-width: 0;
  }
`;

export const StyledErrorMessage = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xsmall};
  color: ${({ theme }) => theme.colors.red.x600};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

// Helper functions for border colors
const getBorderColor = (theme: Theme, variant?: MDSComboboxVariant) => {
  switch (variant) {
    case MDSComboboxVariant.Brand:
      return theme.colors.border.input.brand.default;
    case MDSComboboxVariant.Chrome:
      return theme.colors.border.input.default.default;
    case MDSComboboxVariant.Emphasis:
      return theme.colors.border.input.default.default;
    default:
      return theme.colors.border.input.default.default;
  }
};

const getFocusBorderColor = (theme: Theme, variant?: MDSComboboxVariant) => {
  switch (variant) {
    case MDSComboboxVariant.Brand:
      return theme.colors.border.input.brand.focused;
    case MDSComboboxVariant.Chrome:
      return theme.colors.border.input.default.focused;
    case MDSComboboxVariant.Emphasis:
      return theme.colors.border.input.emphasis.focused;
    default:
      return theme.colors.border.input.default.focused;
  }
};

const getBackgroundColor = (theme: Theme, variant?: MDSComboboxVariant) => {
  switch (variant) {
    case MDSComboboxVariant.Brand:
      return theme.colors.background.input.onAccent.default;
    case MDSComboboxVariant.Chrome:
      return theme.colors.background.input.chrome.default;
    case MDSComboboxVariant.Emphasis:
      return theme.colors.background.input.default.default;
    default:
      return theme.colors.background.input.default.default;
  }
};

const getFocusBackgroundColor = (theme: Theme, variant?: MDSComboboxVariant) => {
  switch (variant) {
    case MDSComboboxVariant.Brand:
      return theme.colors.background.input.onAccent.focused;
    case MDSComboboxVariant.Chrome:
      return theme.colors.background.input.chrome.focused;
    case MDSComboboxVariant.Emphasis:
      return theme.colors.background.input.default.focused;
    default:
      return theme.colors.background.input.default.focused;
  }
};

const getHoverBorderColor = (theme: Theme, variant?: MDSComboboxVariant) => {
  return getFocusBorderColor(theme, variant);
};

const getBorderRadius = (theme: Theme, variant?: MDSComboboxVariant) => {
  switch (variant) {
    case MDSComboboxVariant.Brand:
    case MDSComboboxVariant.Chrome:
    case MDSComboboxVariant.Emphasis:
      return theme.borderRadius.xlarge;
    default:
      return theme.borderRadius.large;
  }
};

const getPadding = (theme: Theme, variant?: MDSComboboxVariant, hasLeftActionButton?: boolean) => {
  switch (variant) {
    case MDSComboboxVariant.Brand:
      return `${theme.spacing[3]} ${theme.spacing[2]} ${theme.spacing[3]} ${hasLeftActionButton ? theme.spacing[3] : theme.spacing[5]}`;
    case MDSComboboxVariant.Emphasis:
      return `0 ${theme.spacing[5]} 0`;
    case MDSComboboxVariant.Chrome:
      return `0 ${theme.spacing[5]}`;
    default:
      return `${theme.spacing[1]} ${theme.spacing[5]}`;
  }
};

// Custom popover for combobox that removes default padding
export const StyledComboboxPopover = styled(MDSPopover)`
  padding: 0;
  background: ${({ theme }) => theme.colors.surface.menu};
`;

export const StyledPopoverContent = styled.div`
  box-sizing: border-box;
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing.xs};
  background: ${({ theme }) => theme.colors.surface.menu};
  border-radius: ${({ theme }) => theme.borderRadius.mediumLarge}px;
  width: var(--combobox-popover-trigger-width);
  min-width: var(--combobox-popover-trigger-width);
`;

export const StyledEmptyResult = styled.div`
  padding: 1rem;
  text-align: left;
`;

export const StyledPopoverSearchContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.xs};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;
